import { DatePicker, Form, Input, message, Select } from 'antd';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormSection from 'components/ui/form-section';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FileUploader from 'components/features/file-uploader';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  EReviewStatus,
  SubmissionResponseDtoStatusEnum,
  UpdateSubmissionStatusPayloadDtoStatusEnum,
  UpsertReviewNotePayloadDto,
} from 'libs/pinnacle-sdk';
import FormActionButtons from 'components/ui/form-action-buttons';
import { initialValues, getReviewStatusOptions, fields } from './form';
import { getValidationSchema } from './form';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { reviewNoteApi, submissionApi } from 'services/apis';
import { isEqual, isNil } from 'lodash';
import dayjs from 'dayjs';
import { useFileSession } from 'hooks/useFileSession';
import { isSubmissionEditable } from 'utils/submission';
import { SortedSelect } from 'components/ui/sorted-select';

const { TextArea } = Input;

interface ReviewNotesProps {
  initialFileSessionId?: string;
  navigateNext: () => void;
  submissionId: string;
  submissionStatus: SubmissionResponseDtoStatusEnum;
}

const ReviewNotes: React.FC<ReviewNotesProps> = ({
  navigateNext,
  submissionId,
  submissionStatus,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const { data: reviewNote, isLoading: isLoadingReviewNote } = useQuery(
    [EQueryKeys.REVIEW_NOTE_BY_SUBMISSION_ID, submissionId],
    () => reviewNoteApi.getReviewNoteBySubmissionId({ submissionId }),
    {
      refetchOnWindowFocus: false,
    }
  );
  const queryClient = useQueryClient();

  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submissionStatus);
  }, [submissionStatus]);

  const { fileSessionId, fileList, setFileList } = useFileSession(
    reviewNote?.files
  );

  const initialData = useMemo(
    () =>
      !isNil(reviewNote)
        ? {
            ...reviewNote,
            approvedDate: reviewNote.approvedDate
              ? dayjs(reviewNote.approvedDate)
              : undefined,
            underwritingNotes: reviewNote.underwritingNotes,
          }
        : undefined,
    [reviewNote]
  );

  const updateSubmissionStatusMutation = useMutation(
    (status: UpdateSubmissionStatusPayloadDtoStatusEnum) =>
      submissionApi.updateSubmissionStatus({
        submissionId,
        updateSubmissionStatusPayloadDto: { status },
      }),
    {
      onSuccess: async (
        _,
        status: UpdateSubmissionStatusPayloadDtoStatusEnum
      ) => {
        queryClient
          .refetchQueries([EQueryKeys.SUBMISSION_BY_ID, submissionId])
          .then(() => {
            if (status === SubmissionResponseDtoStatusEnum.Quoted) {
              navigateNext();
            }
          });
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
        });
      },
    }
  );

  const upsertReviewNoteMutation = useMutation<
    void,
    { response: Response },
    UpsertReviewNotePayloadDto
  >(
    async (values) => {
      await reviewNoteApi.upsertReviewNoteBySubmissionId({
        submissionId,
        upsertReviewNotePayloadDto: {
          ...values,
        },
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('peerReview.saveReviewNoteFailed'),
        });
      },
      onSuccess: async (_, values) => {
        message.success(t('peerReview.saveReviewSuccess'));

        queryClient
          .refetchQueries([EQueryKeys.SUBMISSION_BY_ID, submissionId])
          .then(() => {
            if (
              values.reviewStatus === EReviewStatus.Approved ||
              values.reviewStatus === EReviewStatus.ApprovedWithUpdates
            ) {
              navigateNext();
            }
          });
      },
    }
  );

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
      // Validate form after setting initial values
      validateForm<UpsertReviewNotePayloadDto>(
        initialData as UpsertReviewNotePayloadDto,
        form,
        fields,
        getValidationSchema()
      ).then(setIsFormValid);
    }
  }, [form, initialData, t]);

  const handleOnFinish = useCallback(
    async (values: UpsertReviewNotePayloadDto) => {
      const sanitizedValues = sanitizeFormValue(values);
      const initialValues = {
        approvedDate: initialData ? initialData.approvedDate : undefined,
        peerReviewNotes: initialData ? initialData.peerReviewNotes : undefined,
        reviewStatus: initialData ? initialData.reviewStatus : undefined,
      };
      const currentValues = {
        approvedDate: sanitizedValues.approvedDate,
        peerReviewNotes: sanitizedValues.peerReviewNotes,
        reviewStatus: sanitizedValues.reviewStatus,
      };
      const fileIds = fileList
        .filter((file) => !!file.id)
        .map((file) => file.id) as string[];
      const initialFileIds = reviewNote?.files
        ? reviewNote.files?.map((file) => file.fileId)
        : [];
      // upsertReviewNoteMutation will update the submission status to Quoted
      if (
        !isEqual(initialValues, currentValues) ||
        !isEqual(initialFileIds, fileIds)
      ) {
        upsertReviewNoteMutation.mutate({
          approvedDate: currentValues.approvedDate,
          peerReviewNotes: currentValues.peerReviewNotes,
          reviewStatus: currentValues.reviewStatus,
          fileIds,
        });
      } else {
        updateSubmissionStatusMutation.mutate(
          UpdateSubmissionStatusPayloadDtoStatusEnum.Quoted
        );
      }
    },
    [
      fileList,
      initialData,
      reviewNote?.files,
      updateSubmissionStatusMutation,
      upsertReviewNoteMutation,
    ]
  );

  return (
    <Form
      layout="vertical"
      form={form}
      initialValues={initialValues}
      onValuesChange={async () => {
        const values = form.getFieldsValue();
        const isValid = await validateForm<UpsertReviewNotePayloadDto>(
          values,
          form,
          fields,
          getValidationSchema()
        );
        setIsFormValid(isValid);
      }}
      onFinish={handleOnFinish}
    >
      {/* Underwriting Notes Section */}
      <FormSection title={t('peerReview.underwritingNotes')}>
        <FormLayoutGroup mdColumns={1}>
          <Form.Item name="underwritingNotes">
            <TextArea
              rows={4}
              placeholder={t('peerReview.addYourNotes')}
              size="large"
              disabled={!isEditing}
            />
          </Form.Item>
        </FormLayoutGroup>
      </FormSection>

      {/* Edit and Save Buttons */}
      <div className="mt-6 mb-6">
        <FormActionButtons
          confirmText={t('general.save')}
          isConfirmDisabled={!isEditing}
          confirmOutlined={false}
          isCancelDisabled={!isEditingMode}
          cancelText={t('general.edit')}
          cancelColor={'primary'}
          onCancel={() => setIsEditing(true)}
          onConfirm={async () => {
            setIsEditing(false);
            const currentNote = form.getFieldValue('underwritingNotes');
            const initialNote = initialData?.underwritingNotes;
            if (!isEqual(currentNote, initialNote)) {
              await upsertReviewNoteMutation.mutateAsync({
                underwritingNotes: currentNote,
              });
            }

            message.success(t('peerReview.saveNotesSuccess'));
          }}
        />
      </div>

      {/* Peer Review Notes Section */}
      <FormSection title={t('peerReview.peerReview')}>
        <FormLayoutGroup mdColumns={2}>
          <Form.Item label={t('peerReview.reviewStatus')} name="reviewStatus">
            <SortedSelect
              placeholder={t('general.status')}
              size="large"
              disabled={!isEditingMode}
              options={getReviewStatusOptions(t)}
            />
          </Form.Item>

          <Form.Item label={t('peerReview.approvedDate')} name="approvedDate">
            <DatePicker
              size="large"
              format="DD/MM/YYYY"
              disabled={!isEditingMode}
              placeholder="DD/MM/YYYY"
              style={{ width: '100%' }}
            />
          </Form.Item>
        </FormLayoutGroup>

        <FormLayoutGroup mdColumns={1}>
          <Form.Item
            label={t('peerReview.peerReviewNotes')}
            name="peerReviewNotes"
          >
            <TextArea
              rows={4}
              disabled={!isEditingMode}
              placeholder={t('peerReview.addYourNotes')}
              size="large"
            />
          </Form.Item>

          <Form.Item className="mt-6">
            {fileSessionId && (
              <FileUploader
                sessionId={fileSessionId}
                disabled={!isEditingMode}
                entityType={
                  CreateUploadedFilePayloadDtoEntityTypeEnum.ReviewNote
                }
                fileList={fileList}
                setFileList={setFileList}
              />
            )}
          </Form.Item>
        </FormLayoutGroup>
      </FormSection>

      {/* Mark as Peer Reviewed Button */}
      <div className="mt-6">
        <FormActionButtons
          includeCancel={false}
          confirmText={t('peerReview.markAsPeerReviewed')}
          isConfirmDisabled={
            !isFormValid || isLoadingReviewNote || !isEditingMode
          }
          confirmOutlined={false}
        />
      </div>
    </Form>
  );
};

export default ReviewNotes;
